<!-- #region HTML -->
<template>
  <!-- #region App Container -->
  <div class="app">
    <!-- #region Alert Component -->
    <alert :SystemErrorAlert="isSystemError" @update:SystemErrorAlert="isSystemError = $event"></alert>
    <!-- #endregion -->

    <!-- #region Main Container -->
    <div class="main-container form-container">
      <!-- #region Steps Section -->
      <div class="steps">
        <div class="right-box">
          <div class="sub-container">
            <div class="title">
              <label>新規登録</label>
            </div>
            <div class="step-box text-left">
              <label class="step-title">ステップ 1/2</label>
              <label class="step-description sp-hide">ログイン時に利用するメールアドレスとパスワードを入力してください。</label>
            </div>
          </div>
          <div class="step-description-box text-left">
            <div class="step-description">ログイン時に利用するメールアドレスとパスワードを入力してください。</div>
          </div>
          <div class="text-center signup-notices">
            <p class="promotional-text">* 「英辞郎 on the WEB Pro」無料体験（初回2週間）、「英辞郎 on the WEB Pro Lite」の利用には新規登録が必要です。</p>
            <p class="promotional-text">* 「英辞郎 on the WEB」サービスの違いは<a
                class="txt-decor-underline theme-primary-text" target="blank"
                href="https://eow-support.alc.co.jp/hc/ja/articles/58293847680025">こちら</a></p>
          </div>
        </div>
      </div>
      <!-- #endregion -->

      <!-- #region Form Section -->
      <form novalidate @submit.prevent="validateUser">
        <md-card class="form-container">
          <!-- #region Email Field -->
          <div class="md-form">
            <md-field :class="getValidationClass('email')">
              <md-input id="email" v-model="form.email" name="email" type="email" placeholder="メールアドレスID"
                :disabled="sending" @input="resetAliasExists" />
              <span v-if="!$v.form.email.required" class="md-error error">メールアドレスIDを入力してください。</span>
              <span v-else-if="!$v.form.email.email" class="md-error error">入力されたメールアドレスに誤りはございませんか？</span>
              <span v-else-if="isAliasExists"
                class="md-error error alias-error">このメールアドレスは既に使用されています。他のメールアドレスでご登録ください。</span>
            </md-field>
          </div>
          <!-- #endregion -->

          <!-- #region Password Field -->
          <div class="md-form">
            <md-field :class="getValidationClass('password')">
              <md-input ref="password" id="password" v-model="form.password" name="password" :type="passwordInputType"
                placeholder="パスワード ※8文字以上64文字以下" :disabled="sending" :class="passwordStrengthClass"
                @input="checkPasswordStrength" />
              <div class="password-strength-bar">
                <span v-for="n in 5" :key="n" :class="getSegmentClass(n)" class="strength-segment"></span>
              </div>
              <div class="password-strength" :class="passwordStrengthClass">
                <span v-if="passwordStrengthMessage">{{ passwordStrengthMessage }}</span>
              </div>
              <span class="toggle-password" @click="togglePassword()">
                <img :src="passwordInputType === 'text' ? eyeOpenImg : eyeCloseImg" alt="パスワードを表示" width="100%" />
              </span>
              <span v-if="!$v.form.password.required" class="md-error error mb-10">パスワードを入力してください。</span>
              <span v-else-if="!$v.form.password.minLength" class="md-error error mb-10">8文字以上、64文字以下で設定してください。</span>
              <span v-else-if="!$v.form.password.maxLength" class="md-error error mb-10">8文字以上、64文字以下で設定してください。</span>
              <span v-else-if="showPasswordLengthError" class="md-error error mb-10">8文字以上、64文字以下で設定してください。</span>
            </md-field>
          </div>
          <!-- #endregion -->

          <!-- #region Password Strength Alert -->
          <div class="pw-alert" :class="passwordStrengthClass"
            v-if="form.password.length > 7 && form.password.length < 65 && (isPasswordAcceptable === true || isPasswordWeak === true)">
            <div class="alert-sp text-left">
              <div class="pw-warning" v-if="passwordWarning">{{ passwordWarning }}</div>
              <ul v-if="passwordSuggestions.length" class="password-suggestions">
                <li v-for="(suggestion, index) in passwordSuggestions" :key="index">{{ suggestion }}</li>
              </ul>
            </div>
          </div>
          <!-- #endregion -->

          <!-- #region Password Confirm Field -->
          <div class="md-form">
            <md-field :class="getValidationClass('passwordConfirm')">
              <md-input id="password-confirm" v-model="form.passwordConfirm" name="password-confirm"
                :type="passwordConfirmInputType" placeholder="パスワード(確認) ※8文字以上64文字以下" :disabled="sending"
                @focus="onPasswordConfirmFocus" />
              <span class="toggle-password" @click="togglePasswordConfirm()">
                <img :src="passwordConfirmInputType === 'text' ? eyeOpenImg : eyeCloseImg" alt="パスワード(確認)を表示"
                  width="100%" />
              </span>
              <span v-if="!$v.form.passwordConfirm.required" class="md-error error">パスワード(確認)を入力してください。</span>
              <span v-else-if="!$v.form.passwordConfirm.minLength" class="md-error error">8文字以上、64文字以下で設定してください。</span>
              <span v-else-if="!$v.form.passwordConfirm.maxLength" class="md-error error">8文字以上、64文字以下で設定してください。</span>
              <span v-else-if="!$v.form.passwordConfirm.sameAsPassword"
                class="md-error error">パスワードとパスワード(確認)が、異なっています。</span>
            </md-field>
          </div>
          <!-- #endregion -->

          <!-- #region Opt-In Email Checkbox -->
          <div id="form-is-opt-in-email">
            <span class="left-aligned-span">
              <md-checkbox v-model="isOptInEmail" true-value="true" false-value="false" class="signup-opt-in-checkbox">
                アルクからのメールマガジン等の配信を希望する
              </md-checkbox>
            </span>
            <p class="text-left signup-opt-in-subnote">
              アルクからのメールマガジン等の配信を希望しない場合はチェックを外してください。チェックが入ったままの場合、送信に同意いただいたものとみなします。
            </p>
          </div>
          <!-- #endregion -->

          <!-- #region Progress Bar -->
          <md-progress-bar v-if="sending" md-mode="indeterminate" />
          <!-- #endregion -->

          <!-- #region Registration Warning -->
          <div class="text-left mb-30">
            <span class="warning">登録の完了に必要な認証コードが、ご記入のアドレス宛てにメール送信されます。<br class="sp-hide">
              メールアドレスには必ず「＠alc.co.jp」からのメールを受け取れるものを登録します。</span>
            <span class="warning"><a
                href="https://www.alc.co.jp/policy/other/#%E8%8B%B1%E8%BE%9E%E9%83%8E-on-the-WEB-Pro%E5%88%A9%E7%94%A8%E8%A6%8F%E7%B4%84"
                target="_blank">詳しくはこちら</a></span>
          </div>
          <!-- #endregion -->

          <!-- #region Submit Container -->
          <div class="submit-container">
            <div class="submit-container-padding">
              <span class="submit-container-description" id="policy-info">ご登録の前には、必ず「<a
                  href="https://www.alc.co.jp/policy/other/" target="_blank">利用規約</a>」と 「<a
                  href="https://www.alc.co.jp/policy/personal/"
                  target="_blank">個人情報の取扱いについて</a>」をお読みいただき、同意の上でご登録ください。</span>
            </div>
            <md-card-actions>
              <md-button type="submit" class="primary-button" :disabled="sending || isPasswordWeak || !isFormValid">
                同意して登録する
              </md-button>
            </md-card-actions>
          </div>
          <!-- #endregion -->
        </md-card>
      </form>
      <!-- #endregion -->
    </div>
    <!-- #endregion -->
  </div>
  <!-- #endregion -->
</template>
<!-- #endregion -->

<!-- #region JavaScript -->

<script>
import Alert from '@/layout/Alerts.vue';
import { checkPasswordStrength, getSegmentClass } from '@/utils/passwordUtils';
import { Auth } from 'aws-amplify';
import { validationMixin } from 'vuelidate';
import { email, maxLength, minLength, required, sameAs } from 'vuelidate/lib/validators';
import { togglePassword, togglePasswordConfirm, isFormValid } from '@/utils/base.js';

export default {
  name: 'Signup',
  mixins: [validationMixin],
  computed: {
    isFormValid() {
      return isFormValid(this.$v);
    },
  },
  components: {
    "alert": Alert
  },
  data: () => ({
    form: {
      email: null,
      password: '',
      passwordConfirm: null,
    },
    isOptInEmail: "true",
    sending: false,
    isAliasExists: false,
    isSystemError: false,
    isPasswordWeak: false,
    isPasswordAcceptable: false,
    eyeOpenImg: require('@/assets/img/eye-open.svg'),
    eyeCloseImg: require('@/assets/img/eye-close.svg'),
    passwordStrengthClass: '',
    passwordStrengthMessage: '',
    passwordWarning: '',
    passwordSuggestions: [],
    passwordStrengthScore: 0,
    passwordInputType: 'password',
    passwordConfirmInputType: 'password',
    showPasswordLengthError: false,
  }),
  created() {
    this.$store.commit('setReferrer', document.referrer || '');
  },
  validations: {
    form: {
      email: {
        required,
        email,
      },
      password: {
        required,
        minLength: minLength(8),
        maxLength: maxLength(64),
      },
      passwordConfirm: {
        required,
        minLength: minLength(8),
        maxLength: maxLength(64),
        sameAsPassword: sameAs('password'),
      },
    },
  },
  methods: {
    getValidationClass(fieldName) {
      const field = this.$v.form[fieldName];
      if (field) {
        if (fieldName === 'password' && this.showPasswordLengthError) {
          return { 'md-invalid': true };
        }
        if (fieldName === 'email') {
          if (field.$anyError || this.isAliasExists) {
            return { 'md-invalid': true };
          }
        }
        return {
          'md-invalid': field.$invalid && field.$dirty,
        };
      }
    },
    resetAliasExists() {
      this.isAliasExists = false;
    },
    clearErrorFlag() {
      this.isAliasExists = false;
      this.isSystemError = false;
    },
    async saveUser() {
      this.clearErrorFlag();
      this.sending = true;
      let p = null;
      let secretKey = null;
      let iv = null;
      try {
        secretKey = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
        iv = crypto.getRandomValues(new Uint8Array(16));
        const encodePassword = new TextEncoder().encode(this.form.password);
        p = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, secretKey, encodePassword);

        await Auth.signUp({ username: this.form.email, password: this.form.password });
      } catch (error) {
        if (error.code === 'UsernameExistsException') {
          try {
            await Auth.resendSignUp(this.form.email);
          } catch (error2) {
            this.isAliasExists = true;
            this.isSystemError = false;
            this.sending = false;
            window.scrollTo(0, 0);
            return;
          }
        } else {
          this.isSystemError = true;
          this.isAliasExists = false;
          this.sending = false;
          window.scrollTo(0, 0);
          return;
        }
      }
      this.sending = false;
      this.$store.commit('setSignupEmail', this.form.email);
      this.$store.commit('setIsOptInEmail', this.isOptInEmail);
      this.$store.commit('setP', JSON.stringify({ v: btoa(Array.from(new Uint8Array(p), char => String.fromCharCode(char)).join('')), iv: Array.from(iv) }));
      this.$store.commit('setK', secretKey);

      this.$router.push({
        name: 'verifySignup',
      });
    },
    async validateUser() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.checkPasswordStrength();
      if (this.isPasswordWeak) {
        this.isAliasExists = false;
        this.isSystemError = false;
        window.scrollTo(0, 0);
        return;
      }
      this.isPasswordWeak = false;

      this.saveUser();
    },
    togglePassword() {
      this.passwordInputType = togglePassword(this.passwordInputType);
    },
    togglePasswordConfirm() {
      this.passwordConfirmInputType = togglePasswordConfirm(this.passwordConfirmInputType);
    },
    checkPasswordStrength() {
      const passwordStrength = checkPasswordStrength(this.form.password);
      this.passwordStrengthClass = passwordStrength.class;
      this.passwordStrengthMessage = passwordStrength.message;
      this.passwordWarning = passwordStrength.warning;
      this.passwordSuggestions = passwordStrength.suggestions;
      this.passwordStrengthScore = passwordStrength.score;
      this.isPasswordWeak = passwordStrength.isPasswordWeak;
      this.isPasswordAcceptable = passwordStrength.isPasswordAcceptable;
      this.showPasswordLengthError = false;
    },
    getSegmentClass(index) {
      return getSegmentClass(index, this.form.password.length, this.passwordStrengthScore);
    },
    onPasswordConfirmFocus() {
      this.showPasswordLengthError = !this.$v.form.password.minLength || !this.$v.form.password.maxLength;
    },
  },
};
</script>
<!-- #endregion -->

<!-- #region CSS -->
<style scoped>
.submit-container-padding {
  padding: 30px 15% 12px 15%;
}

.signup-notices .promotional-text {
  margin: 0;
}

.signup-notices .promotional-text + .promotional-text {
  margin-top: 12px;
}

.promotional-text {
  font-family: var(--default-family);
  color: var(--theme-secondary-color);
  font-size: var(--text-size-XS);
  font-weight: var(--weight-normal);
  line-height: var(--line-height-primary);
  align-self: stretch;
}

#form-is-opt-in-email ::v-deep .signup-opt-in-checkbox.md-checkbox {
  align-items: flex-start;
}

.signup-opt-in-subnote {
  margin: 10px 0 0 0;
  font-family: var(--default-family);
  color: var(--text-primary);
  font-size: var(--text-size-XS);
  font-weight: var(--weight-normal);
  line-height: 18px;
  text-align: left;
}

#form-is-opt-in-email ::v-deep .signup-opt-in-checkbox .md-checkbox-label {
  height: auto !important;
  font-size: var(--text-size-XS) !important;
  line-height: 18px !important;
}

/* #region SP */
@media (max-width: 768px) {
  .submit-container-padding {
    padding: 20px 5% 10px 5%;
  }
}

/* #endregion */
</style>
<!-- #endregion -->
